import axios, { AxiosInstance } from 'axios';
import {
  LoginRequest,
  LoginResponse,
  User,
  TaskStatus,
  Plan,
  Subscription,
  AdminUserRow,
  PaymentRow,
  PromoRow
} from '../types';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || (window.location.origin.replace(':80', ':8000').replace(':3000', ':8000'));

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Добавляем интерцептор для добавления токена к запросам
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Интерцептор для обработки ошибок авторизации
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('access_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Авторизация
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const formData = new FormData();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);

    const response = await this.api.post<LoginResponse>('/auth/login', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    // Сохраняем токен
    localStorage.setItem('access_token', response.data.access_token);
    
    return response.data;
  }

  async register(userData: { email: string; password: string }): Promise<User> {
    const response = await this.api.post<User>('/auth/register', userData);
    return response.data;
  }

  logout(): void {
    localStorage.removeItem('access_token');
  }

  async getMe(): Promise<User> {
    const response = await this.api.get<User>('/auth/me');
    return response.data;
  }

  async updateProfile(data: { full_name?: string; phone?: string }): Promise<User> {
    const response = await this.api.patch<User>('/auth/me', data);
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  async getMyPayments(): Promise<PaymentRow[]> {
    const response = await this.api.get<PaymentRow[]>('/billing/payments');
    return response.data;
  }

  // --- Billing ---
  async getPlans(): Promise<Plan[]> {
    const response = await this.api.get<Plan[]>('/billing/plans');
    return response.data;
  }

  async getSubscription(): Promise<Subscription> {
    const response = await this.api.get<Subscription>('/billing/subscription');
    return response.data;
  }

  async checkout(planCode: string, acceptOffer: boolean, email?: string): Promise<{ payment_url: string; order_id: string }> {
    const response = await this.api.post('/billing/checkout', {
      plan_code: planCode,
      accept_offer: acceptOffer,
      email,
    });
    return response.data;
  }

  async redeemPromo(code: string): Promise<{ ok: boolean; message: string; subscription: Subscription }> {
    const response = await this.api.post('/billing/redeem', { code });
    return response.data;
  }

  // --- Admin ---
  async adminListUsers(): Promise<AdminUserRow[]> {
    const response = await this.api.get<AdminUserRow[]>('/admin/users');
    return response.data;
  }

  async adminGrant(userId: number, body: { plan_code?: string; duration_days?: number; request_quota?: number; source?: string }): Promise<any> {
    const response = await this.api.post(`/admin/users/${userId}/grant`, body);
    return response.data;
  }

  async adminGrantGrace(durationDays?: number): Promise<any> {
    const response = await this.api.post('/admin/grant-grace', { duration_days: durationDays });
    return response.data;
  }

  async adminSetAdmin(userId: number, isAdmin: boolean): Promise<any> {
    const response = await this.api.post(`/admin/users/${userId}/set-admin`, { is_admin: isAdmin });
    return response.data;
  }

  async adminListPayments(): Promise<PaymentRow[]> {
    const response = await this.api.get<PaymentRow[]>('/admin/payments');
    return response.data;
  }

  async adminListPromo(): Promise<PromoRow[]> {
    const response = await this.api.get<PromoRow[]>('/admin/promo');
    return response.data;
  }

  async adminCreatePromo(body: { code: string; duration_days: number; description?: string; max_uses?: number; one_per_user?: boolean }): Promise<any> {
    const response = await this.api.post('/admin/promo', body);
    return response.data;
  }

  // New API (prices + requests)
  async uploadPriceBase(files: File[]): Promise<any> {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    const response = await this.api.post('/prices/base/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getPriceBase(): Promise<any> {
    const response = await this.api.get('/prices/base');
    return response.data;
  }

  /** Заявка может состоять из нескольких файлов (фото по листам, таблица + допник). */
  async uploadRequest(files: File | File[]): Promise<any> {
    const form = new FormData();
    const list = Array.isArray(files) ? files : [files];
    list.forEach(f => form.append('files', f));
    const response = await this.api.post('/requests/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data;
  }

  /** Пользователь проверил распознанное — запускаем поиск по прайсу. */
  async confirmPreview(id: number, rows?: any[]): Promise<{ ok: boolean; task_id: string }> {
    const response = await this.api.post(`/requests/${id}/preview/confirm`, rows ? { rows } : {});
    return response.data;
  }

  async listRequests(): Promise<any[]> {
    const response = await this.api.get('/requests/');
    return response.data;
  }

  async getRequest(id: number): Promise<any> {
    const response = await this.api.get(`/requests/${id}`);
    return response.data;
  }

  async overwriteAnalysis(id: number, rows: any[]): Promise<{ ok: boolean; task_id: string }> {
    const response = await this.api.post(`/requests/${id}/analysis/overwrite`, { rows });
    return response.data;
  }

  async deleteRequest(id: number): Promise<void> {
    await this.api.delete(`/requests/${id}`);
  }

  // Legacy uploadPdf removed; use uploadRequest

  // Статус задач
  async getTaskStatus(taskId: string): Promise<TaskStatus> {
    const response = await this.api.get<TaskStatus>(`/tasks/task-status/${taskId}`);
    return response.data;
  }

  // Legacy VOR/project APIs removed

  // Скачивание файлов
  async downloadFile(filePath: string): Promise<Blob> {
    const response = await this.api.post('/files/download', {
      file_path: filePath,
    }, {
      responseType: 'blob',
    });
    return response.data;
  }

  // For PDF/Excel preview, always use downloadFile to get Blob and createObjectURL on client

  // Утилиты
  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  getAuthToken(): string | null {
    return localStorage.getItem('access_token');
  }
}

export const apiService = new ApiService();
export default apiService; 